/**
 * Format Regios amount safely.
 */
function formatRegiosAmount(amount) {
  const numberAmount = Number(amount);
  return Number.isFinite(numberAmount) ? numberAmount.toFixed(2) : amount;
}

/**
 * Build the Regios sale-price HTML block that matches the theme's f-price markup.
 */
function buildRegiosPriceHTML(salePrice, regularPrice) {
  return `<div class="f-price__sale" data-made-visible-by-regios-dopp="true" style="display:flex;">
    <span class="visually-hidden visually-hidden--inline">Sale price</span>
    <span class="f-price-item f-price-item--sale" data-made-visible-by-regios-dopp="true">$${formatRegiosAmount(salePrice.amount)}</span>
    <span class="visually-hidden visually-hidden--inline">Regular price</span>
    <span class="f-price-item f-price-item--regular" style="text-decoration: line-through;display: initial;">$${formatRegiosAmount(regularPrice.amount)}</span>
  </div>`;
}

/**
 * Regios cache.
 */
const regiosProductJsonCache = new Map();
const regiosAnyVariantSaleCache = new Map();
const regiosSpecificVariantSaleCache = new Map();

function normalizeProductHandle(handle) {
  if (!handle) return '';

  return String(handle)
    .replace(/^\/?products\//, '')
    .split('?')[0]
    .split('#')[0]
    .split('/')[0]
    .trim();
}

function getCardRootFromPrice(priceEl) {
  return (
    priceEl.closest('.variable-products[data-variant-id]') ||
    priceEl.closest('[data-product-handle]') ||
    priceEl.closest('[data-handle]') ||
    priceEl.closest('[data-variant-id]') ||
    priceEl.closest('.product-card') ||
    priceEl.closest('.product-column') ||
    priceEl.closest('.card')
  );
}

function getProductHandleFromCard(card) {
  if (!card) return '';

  const directHandle =
    card.dataset.productHandle ||
    card.dataset.handle ||
    card.getAttribute('data-product-handle') ||
    card.getAttribute('data-handle');

  if (directHandle) {
    return normalizeProductHandle(directHandle);
  }

  const productLink = card.querySelector('a[href*="/products/"]');
  if (!productLink) return '';

  const href = productLink.getAttribute('href') || '';
  const match = href.match(/\/products\/([^/?#]+)/);

  return match && match[1] ? normalizeProductHandle(match[1]) : '';
}

function getVariantIdFromCard(card) {
  if (!card) return undefined;

  let variantId = parseInt(card.dataset.variantId, 10);

  if (!variantId) {
    const variantInput = card.querySelector('[name="id"]');
    variantId = variantInput ? parseInt(variantInput.value, 10) : undefined;
  }

  return variantId || undefined;
}

function getProductIdFromCard(card) {
  if (!card) return undefined;

  let productId = parseInt(card.dataset.productId, 10);

  if (!productId) {
    const productIdEl = card.querySelector('[data-product-id]');
    productId = productIdEl ? parseInt(productIdEl.dataset.productId, 10) : undefined;
  }

  return productId || undefined;
}

async function getProductJsonByHandle(handle) {
  if (!handle) return null;

  if (regiosProductJsonCache.has(handle)) {
    return regiosProductJsonCache.get(handle);
  }

  const root = window.Shopify?.routes?.root || '/';
  const baseRoot = root.endsWith('/') ? root : `${root}/`;
  const productUrl = `${baseRoot}products/${handle}.js`;

  const response = await fetch(productUrl);

  if (!response.ok) {
    throw new Error(`Product JSON not found: ${handle}`);
  }

  const product = await response.json();
  regiosProductJsonCache.set(handle, product);

  return product;
}

function isRegiosSale(data) {
  const { salePrice, regularPrice } = data || {};

  return (
    salePrice &&
    regularPrice &&
    Number(regularPrice.amount) > Number(salePrice.amount)
  );
}

/**
 * For app variant cards on collection/search.
 * App card has data-variant-id, so apply discount for that exact variant.
 */
async function getRegiosSaleForSpecificVariant(card, priceEl) {
  if (!card || !window.RegiosDOPP?.api?.v0) {
    return {
      checked: false,
      saleData: null,
    };
  }

  const variantId = getVariantIdFromCard(card);

  if (!variantId) {
    return {
      checked: false,
      saleData: null,
    };
  }

  let productId = getProductIdFromCard(card);
  let regularPriceInCents = parseInt(priceEl.dataset.bprice, 10);
  const handle = getProductHandleFromCard(card);

  try {
    if (handle) {
      const product = await getProductJsonByHandle(handle);

      if (product && product.id) {
        productId = parseInt(product.id, 10) || productId;

        const matchedVariant = Array.isArray(product.variants)
          ? product.variants.find((variant) => Number(variant.id) === Number(variantId))
          : null;

        if (matchedVariant && matchedVariant.price) {
          regularPriceInCents = parseInt(matchedVariant.price, 10);
        }
      }
    }

    if (!productId || !variantId || !regularPriceInCents || isNaN(regularPriceInCents)) {
      return {
        checked: false,
        saleData: null,
      };
    }

    const cacheKey = `${productId}:${variantId}:${regularPriceInCents}`;

    if (regiosSpecificVariantSaleCache.has(cacheKey)) {
      return {
        checked: true,
        saleData: regiosSpecificVariantSaleCache.get(cacheKey),
      };
    }

    const data = await window.RegiosDOPP.api.v0.calculateDiscountedPrices({
      productId,
      variantId,
      regularPriceInCents,
    });

    const saleData = isRegiosSale(data) ? data : null;
    regiosSpecificVariantSaleCache.set(cacheKey, saleData);

    return {
      checked: true,
      saleData,
    };
  } catch (e) {
    return {
      checked: false,
      saleData: null,
    };
  }
}

/**
 * For normal product cards.
 * If any variant has Regios sale, show sale price on card.
 */
async function getRegiosSaleFromAnyVariant(card) {
  const handle = getProductHandleFromCard(card);

  if (!handle || !window.RegiosDOPP?.api?.v0) {
    return {
      checked: false,
      saleData: null,
    };
  }

  try {
    const product = await getProductJsonByHandle(handle);

    if (!product || !product.id || !Array.isArray(product.variants)) {
      return {
        checked: false,
        saleData: null,
      };
    }

    const cacheKey = String(product.id);

    if (regiosAnyVariantSaleCache.has(cacheKey)) {
      return {
        checked: true,
        saleData: regiosAnyVariantSaleCache.get(cacheKey),
      };
    }

    const variantChecks = product.variants.map(async (variant) => {
      const variantId = parseInt(variant.id, 10);
      const regularPriceInCents = parseInt(variant.price, 10);

      if (!variantId || !regularPriceInCents || isNaN(regularPriceInCents)) {
        return null;
      }

      try {
        const data = await window.RegiosDOPP.api.v0.calculateDiscountedPrices({
          productId: parseInt(product.id, 10),
          variantId,
          regularPriceInCents,
        });

        return isRegiosSale(data) ? data : null;
      } catch (e) {
        return null;
      }
    });

    const results = await Promise.allSettled(variantChecks);

    const saleResults = results
      .filter((result) => result.status === 'fulfilled' && result.value)
      .map((result) => result.value);

    if (!saleResults.length) {
      regiosAnyVariantSaleCache.set(cacheKey, null);

      return {
        checked: true,
        saleData: null,
      };
    }

    saleResults.sort((a, b) => Number(a.salePrice.amount) - Number(b.salePrice.amount));

    const bestSale = saleResults[0];
    regiosAnyVariantSaleCache.set(cacheKey, bestSale);

    return {
      checked: true,
      saleData: bestSale,
    };
  } catch (e) {
    return {
      checked: false,
      saleData: null,
    };
  }
}

/**
 * Apply Regios discount prices to product cards inside container.
 *
 * options:
 * - preferVariantCard: true  => collection/search app variant card ma exact variant discount apply thase
 * - checkAllVariants: true   => normal product card ma koi pan variant sale hoy to card sale show thase
 */
async function applyRegiosPriceToContainer(container, force = false, options = {}) {
  if (!container) return;
  if (!window.RegiosDOPP?.api?.v0) return;

  const settings =
    typeof options === 'boolean'
      ? { checkAllVariants: options, preferVariantCard: false }
      : {
          checkAllVariants: false,
          preferVariantCard: false,
          ...options,
        };

  const priceEls = container.querySelectorAll('.f-price[data-bprice]');

  const tasks = Array.from(priceEls).map(async (priceEl) => {
    if (!force && priceEl.dataset.regiosApplied) return;

    const card = getCardRootFromPrice(priceEl);
    if (!card) return;

    let finalRegiosData = null;
    let checked = false;

    /**
     * Collection/Search app cards:
     * class="... variable-products ..." and data-variant-id available.
     * So first apply exact variant discount.
     */
    if (settings.preferVariantCard && getVariantIdFromCard(card)) {
      const variantResult = await getRegiosSaleForSpecificVariant(card, priceEl);
      checked = variantResult.checked;
      finalRegiosData = variantResult.saleData;
    }

    /**
     * Predictive/Featured normal product cards:
     * show sale if any variant is on Regios sale.
     */
    if (!checked && settings.checkAllVariants) {
      const anyVariantResult = await getRegiosSaleFromAnyVariant(card);
      checked = anyVariantResult.checked;
      finalRegiosData = anyVariantResult.saleData;
    }

    /**
     * Fallback old logic.
     */
    if (!checked) {
      const regularPriceInCents = parseInt(priceEl.dataset.bprice, 10);

      if (!regularPriceInCents || isNaN(regularPriceInCents)) return;

      const variantId = getVariantIdFromCard(card);
      const productId = getProductIdFromCard(card);

      if (!productId && !variantId) return;

      try {
        finalRegiosData = await window.RegiosDOPP.api.v0.calculateDiscountedPrices({
          productId,
          variantId,
          regularPriceInCents,
        });
      } catch (e) {
        return;
      }
    }

    const fprice = priceEl;
    const oldRegiosSaleEl = fprice.querySelector('.f-price__sale[data-made-visible-by-regios-dopp="true"]');
    const existingSaleEl = fprice.querySelector('.f-price__sale');

    if (isRegiosSale(finalRegiosData)) {
      const { salePrice, regularPrice } = finalRegiosData;
      const saleHTML = buildRegiosPriceHTML(salePrice, regularPrice);

      fprice.classList.add('f-price--on-sale');

      if (existingSaleEl) {
        existingSaleEl.outerHTML = saleHTML;
      } else {
        fprice.insertAdjacentHTML('beforeend', saleHTML);
      }
    } else {
      if (oldRegiosSaleEl) {
        oldRegiosSaleEl.remove();
      }

      const hasThemeSale = fprice.querySelector('.f-price__sale:not([data-made-visible-by-regios-dopp])');

      if (!hasThemeSale) {
        fprice.classList.remove('f-price--on-sale');
      }
    }

    priceEl.dataset.regiosApplied = '1';
  });

  await Promise.allSettled(tasks);
}

// ─────────────────────────────────────────────────────────────────────────────
// Quick View modal custom element
// ─────────────────────────────────────────────────────────────────────────────
if (!customElements.get('quick-view-modal')) {
  customElements.define(
    'quick-view-modal',
    class QuickViewModal extends DrawerComponent {
      constructor() {
        super();

        this.classesToRemoveOnLoad = 'drawer--loading';
        this.drawerBody = this.querySelector(this.selector);
        // this._onVariantChanged = this._handleVariantChanged.bind(this);
      }

      get selector() {
        return '.quick-view__content';
      }

      get sourceSelector() {
        return '#MainProduct-quick-view__content';
      }

      get requiresBodyAppended() {
        return !(Shopify.designMode && this.closest('.section-group-overlay-quick-view'));
      }

      shouleBeShow() {
        const sectionId = this.getProductQuickViewSectionId();
        return typeof sectionId === 'string';
      }

      prepareToShow() {
        super.prepareToShow();
        this.quickview();
      }

      handleAfterShow() {
        super.handleAfterShow();

        // document.addEventListener('variant:changed', this._onVariantChanged);

        document.dispatchEvent(
          new CustomEvent('quick-view:open', {
            detail: { productUrl: this.dataset.productUrl },
          })
        );
      }

      handleAfterHide() {
        super.handleAfterHide();

        // document.removeEventListener('variant:changed', this._onVariantChanged);

        const drawerContent = this.querySelector(this.selector);
        drawerContent.innerHTML = '';
        this.classList.add(this.classesToRemoveOnLoad);
      }

      /**
       * Called whenever product-info.js fires variant:changed on the document.
       * Re-applies Regios prices to the quick view container.
       */
      // async _handleVariantChanged(event) {
      //   const drawerContent = this.querySelector(this.selector);
      //   if (!drawerContent || !drawerContent.innerHTML) return;

      //   const variant = event?.detail?.variant;
      //   if (!variant) return;

      //   // Get product ID and regular price from the new variant
      //   const productId = parseInt(this.dataset.variantId, 10); // note: QV modal stores product id here
      //   const variantId = variant.id;
      //   const regularPriceInCents = variant.price;

      //   if (!productId || !variantId || !regularPriceInCents) return;
      //   if (!window.RegiosDOPP?.api?.v0) return;

      //   try {
      //     const { salePrice, regularPrice } = await window.RegiosDOPP.api.v0.calculateDiscountedPrices({
      //       productId,
      //       variantId,
      //       regularPriceInCents,
      //     });

      //     const isSale = salePrice && regularPrice && regularPrice.amount > salePrice.amount;
      //     const newEl = buildRegiosPriceHTML(salePrice, regularPrice);

      //     const productParent = drawerContent.querySelectorAll('.product__info-container');
      //     productParent.forEach((ele) => {
      //       const priceEle = ele.querySelector('.product__block--price .f-price');
      //       const salePriceEle = ele.querySelector('.product__block--price .f-price__sale');
      //       const bpriceEle = ele.querySelector('.product__block--buy_buttons .f-price');
      //       const bsalePriceEle = ele.querySelector('.product__block--buy_buttons .f-price__sale');

      //       if (isSale) {
      //         priceEle && priceEle.classList.add('f-price--on-sale');
      //         bpriceEle && bpriceEle.classList.add('f-price--on-sale');
      //         if (salePriceEle) salePriceEle.innerHTML = newEl;
      //         if (bsalePriceEle) bsalePriceEle.innerHTML = newEl;
      //       } else {
      //         priceEle && priceEle.classList.remove('f-price--on-sale');
      //         bpriceEle && bpriceEle.classList.remove('f-price--on-sale');
      //       }
      //     });
      //   } catch (e) {
      //     // Silently ignore
      //   }
      // }

      getProductQuickViewSectionId() {
        let sectionId = FoxTheme.QuickViewSectionId || false;

        if (!sectionId) {
          // Get section id from overlay groups.
          const productQuickView = document.querySelector('.section-group-overlay-quick-view');
          if (productQuickView) {
            sectionId = FoxTheme.utils.getSectionId(productQuickView);
          }

          // Cache for better performance.
          FoxTheme.QuickViewSectionId = sectionId;
        }

        return sectionId;
      }

      quickview() {
        const drawerContent = this.querySelector(this.selector);
        const quickViewLoader = this.querySelector('.fixed-overlay + .quick-view-loader');
        
        quickViewLoader?.classList.remove('hidden');

        const sectionId = this.getProductQuickViewSectionId();

        // Use 0ms so the drawer paint happens first, then fetch immediately.
        // Previously this was 1000ms which caused a visible 1-second blank state.
        setTimeout(async () => {
          // Priority order for which variant to load:
          //  1. variant param inside data-product-url  ← AdsGun's onPriceApplied writes this
          //     for the correct sale variant (covers middle/non-first variant on sale)
          //  2. window.selectedQuickViewVariantId      ← set by B4B click listener in theme.js
          //  3. no variant param                       ← product default
          const productUrl = this.dataset.productUrl || '';
          const productId = this.dataset.variantId || '';
          const baseUrl = productUrl.split('?')[0];
          const urlParams = new URLSearchParams(productUrl.split('?')[1] || '');
          const adsgunVariant = urlParams.get('variant');                 // set by AdsGun bridge
          const selected_variant = adsgunVariant || window.selectedQuickViewVariantId;
          const selected_variantPrice = window.selectedVariantPrice;

          //       const { salePrice, regularPrice } = await window.RegiosDOPP.api.v0.calculateDiscountedPrices({
          //         productId: productId,
          //         variantId: selected_variant,
          //         regularPriceInCents: selected_variantPrice,
          //       });
          //       const isSale = salePrice ? regularPrice?.amount > salePrice?.amount : false;
          //       const newEl = `<div class="f-price__sale" data-made-visible-by-regios-dopp="true">
          // <span class="visually-hidden visually-hidden--inline">Sale price</span>
          // <span class="f-price-item f-price-item--sale" data-made-visible-by-regios-dopp="true">$${salePrice?.amount}</span>
          //   <span class="visually-hidden visually-hidden--inline">Regular price</span>
          //   <span class="f-price-item f-price-item--regular" style="text-decoration: line-through;display: initial;">$${regularPrice?.amount}</span></div>`

          //       console.log(salePrice.amount, "salePrice", newEl);
          //       console.log(regularPrice.amount, "regularPrice", isSale);

          // Run Regios API call and section fetch in parallel for maximum speed
          const regiosPromise = (async () => {
            if (!window.RegiosDOPP?.api?.v0 || !productId || !selected_variant || !selected_variantPrice) return null;
            try {
              return await window.RegiosDOPP.api.v0.calculateDiscountedPrices({
                productId: parseInt(productId, 10),
                variantId: parseInt(selected_variant, 10),
                regularPriceInCents: selected_variantPrice,
              });
            } catch (e) {
              return null;
            }
          })();

          const sectionUrl = selected_variant
            ? `${baseUrl}?variant=${selected_variant}&section_id=${sectionId}`
            : `${baseUrl}?section_id=${sectionId}`;
          fetch(sectionUrl)
            .then((response) => response.text())
            .then(async (responseText) => {
              const productElement = new DOMParser()
                .parseFromString(responseText, 'text/html')
                .querySelector(this.sourceSelector);

                quickViewLoader?.classList.add('hidden');

              this.setInnerHTML(drawerContent, productElement.content.cloneNode(true));
              FoxTheme.a11y.trapFocus(this, this.focusElement);

              if (window.Shopify && Shopify.PaymentButton) {
                Shopify.PaymentButton.init();
              }

              setTimeout(() => {
                this.classList.remove(this.classesToRemoveOnLoad);
              }, 300);

              document.dispatchEvent(
                new CustomEvent('quick-view:loaded', {
                  detail: { productUrl: this.dataset.productUrl },
                })
              );
              const thumbsEl = document.querySelector(".block_top-slider-thumb");
              const mainEl = document.querySelector(".block_top-slider-main");

              // Regios
              // const productParent = this.querySelectorAll(".product__info-container");
              // productParent && productParent.forEach((ele) => {
              //   const priceEle = ele.querySelector(".product__block--price .f-price");
              //   const salePriceEle = ele.querySelector(".product__block--price .f-price__sale");
              //   const bpriceEle = ele.querySelector(".product__block--buy_buttons .f-price");
              //   const bsalePriceEle = ele.querySelector(".product__block--buy_buttons .f-price__sale");
              //   if (isSale) {
              //     priceEle.classList.add("f-price--on-sale");
              //     bpriceEle && bpriceEle.classList.add("f-price--on-sale");
              //     salePriceEle.innerHTML = newEl;

              //     if (bsalePriceEle) {
              //       bsalePriceEle.innerHTML = newEl;
              //     }
              //     console.log(bsalePriceEle, "bsalePriceEle");
              //   } else {
              //     priceEle.classList.remove("f-price--on-sale");
              //     bpriceEle && bpriceEle.classList.remove("f-price--on-sale");
              //   }
              // });

              if (thumbsEl && mainEl) {
                // Initialize thumbs
                window.swiperThumbsInstance = new Swiper(thumbsEl, {
                  spaceBetween: 8,
                  slidesPerView: 6,
                  freeMode: true,
                  watchSlidesProgress: true,
                  navigation: {
                    nextEl: ".swiper-button-next-thumb",
                    prevEl: ".swiper-button-prev-thumb",
                  },
                  breakpoints: {
                    320: {
                      // direction: "vertical",
                      slidesPerView: 4,
                      spaceBetween: 5,
                    },
                    768: {
                      // direction: "vertical",
                      slidesPerView: 4,
                      spaceBetween: 8,
                    },
                    1100: {
                      // direction: "vertical",
                      slidesPerView: 5,
                      spaceBetween: 8,
                    },
                  },
                });

                // Initialize main slider
                window.swiperMainInstance = new Swiper(mainEl, {
                  loop: true,
                  spaceBetween: 10,
                  watchSlidesProgress: true,
                  navigation: {
                    nextEl: ".swiper-button-next-main",
                    prevEl: ".swiper-button-prev-main",
                  },
                  thumbs: {
                    swiper: window.swiperThumbsInstance,
                  },
                });
              } else {
                console.warn("Swiper elements not found on the page.");
              }
              if (window.okeWidgetApi) {
                window.okeWidgetApi.initAllWidgets();
              }

              // const regiosData = await regiosPromise;
              // if (regiosData) {
              //   const { salePrice, regularPrice } = regiosData;
              //   const isSale = salePrice && regularPrice && regularPrice.amount > salePrice.amount;
              //   if (isSale) {
              //     const newEl = buildRegiosPriceHTML(salePrice, regularPrice);
              //     const productParent = this.querySelectorAll('.product__info-container');
              //     productParent.forEach((ele) => {
              //       const priceEle = ele.querySelector('.product__block--price .f-price');
              //       const salePriceEle = ele.querySelector('.product__block--price .f-price__sale');
              //       const bpriceEle = ele.querySelector('.product__block--buy_buttons .f-price');
              //       const bsalePriceEle = ele.querySelector('.product__block--buy_buttons .f-price__sale');

              //       priceEle && priceEle.classList.add('f-price--on-sale');
              //       bpriceEle && bpriceEle.classList.add('f-price--on-sale');
              //       if (salePriceEle) salePriceEle.innerHTML = newEl;
              //       if (bsalePriceEle) bsalePriceEle.innerHTML = newEl;
              //     });
              //   }
              // }
            })
            .catch((e) => {
              console.error(e);
            });
        }, 500);
      }

      setInnerHTML(element, innerHTML) {
        element.innerHTML = '';
        element.appendChild(innerHTML);
        element.querySelectorAll('script').forEach((oldScriptTag) => {
          const newScriptTag = document.createElement('script');
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        // document.removeEventListener('variant:changed', this._onVariantChanged);
        this.handleAfterHide();
      }
    }
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Regios integration for Predictive Search popup
// ─────────────────────────────────────────────────────────────────────────────
// We patch the PredictiveSearch.renderSearchResults method after the class is
// defined (it's defined synchronously in search.js which loads before this).
// If search.js loads after, we wait for DOMContentLoaded.

function patchPredictiveSearchRegios() {
  if (typeof PredictiveSearch === 'undefined') return;

  const _originalRender = PredictiveSearch.prototype.renderSearchResults;

  PredictiveSearch.prototype.renderSearchResults = function (responseText) {
    _originalRender.call(this, responseText);

    const resultsContainer = this.querySelector('#predictive-search-results-products-list');
    if (!resultsContainer) return;

    const applyWhenReady = () => {
      if (window.RegiosDOPP?.api?.v0) {
        // Predictive search normal product card che,
        // so koi pan variant sale ma hoy to card sale show thase.
        applyRegiosPriceToContainer(resultsContainer, true, {
          checkAllVariants: true,
          preferVariantCard: false,
        });
      } else {
        window.addEventListener(
          'regios-dopp:api-initialized',
          () => {
            applyRegiosPriceToContainer(resultsContainer, true, {
              checkAllVariants: true,
              preferVariantCard: false,
            });
          },
          { once: true }
        );
      }
    };

    requestAnimationFrame(applyWhenReady);
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Regios integration for Search Results page (/search)
// ─────────────────────────────────────────────────────────────────────────────

function applyRegiosToSearchResultsPage() {
  // Collection ane search result page banne ma app variant cards ave che.
  if (
    !window.location.pathname.includes('/search') &&
    !window.location.pathname.includes('/collections')
  ) {
    return;
  }

  function debounce(func, wait) {
    let timeout;

    return function (...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), wait);
    };
  }

  function getSearchCollectionContainer() {
    return (
      document.getElementById('ProductsList') ||
      document.getElementById('ProductGridContainer') ||
      document.querySelector('.product-grid') ||
      document.querySelector('main')
    );
  }

  function doApply() {
    const currentGrid = getSearchCollectionContainer();
    if (!currentGrid) return;

    if (window.RegiosDOPP?.api?.v0) {
      applyRegiosPriceToContainer(currentGrid, true, {
        // App variant card ma exact selected/data variant par discount.
        preferVariantCard: true,

        // Jo normal product card male to koi pan variant sale hoy to show.
        checkAllVariants: true,
      });
    } else {
      window.addEventListener(
        'regios-dopp:api-initialized',
        () => {
          applyRegiosPriceToContainer(currentGrid, true, {
            preferVariantCard: true,
            checkAllVariants: true,
          });
        },
        { once: true }
      );
    }
  }

  const debouncedApply = debounce(doApply, 100);

  // Initial apply.
  doApply();

  // Theme AJAX filter/re-render event.
  document.addEventListener('collection:rerendered', debouncedApply);

  // App async variant cards update kare che, so observe both attributes and childList.
  if (typeof MutationObserver !== 'undefined') {
    const gridContainer =
      document.getElementById('ProductGridContainer') ||
      document.getElementById('ProductsList') ||
      document.querySelector('main');

    if (gridContainer) {
      const observer = new MutationObserver(debouncedApply);

      observer.observe(gridContainer, {
        childList: true,
        attributes: true,
        subtree: true,
        attributeFilter: [
          'data-variant-id',
          'data-product-handle',
          'data-handle',
          'data-product-id',
          'class',
        ],
      });
    }
  }
}

function applyRegiosToFeaturedCollections() {
  function doApply() {
    if (!window.RegiosDOPP?.api?.v0) return;

    document
      .querySelectorAll('.product-card, .product-column, .product-recommendations')
      .forEach((container) => {
        applyRegiosPriceToContainer(container, true, {
          checkAllVariants: true,
          preferVariantCard: false,
        });
      });
  }

  if (window.RegiosDOPP?.api?.v0) {
    doApply();
  } else {
    window.addEventListener('regios-dopp:api-initialized', doApply, { once: true });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Bootstrap
// ─────────────────────────────────────────────────────────────────────────────

(function initRegiosSearchIntegration() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      patchPredictiveSearchRegios();
      applyRegiosToSearchResultsPage();
      applyRegiosToFeaturedCollections();
    });
  } else {
    patchPredictiveSearchRegios();
    applyRegiosToSearchResultsPage();
    applyRegiosToFeaturedCollections();
  }
})();